import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrendingUp, CheckCircle, Users, ShieldAlert, Star, ArrowRight, Loader2, AlertCircle, Layers } from 'lucide-react';
import {
  reachExtractionsAPI, reachStakeholdersAPI, reachBlockersAPI,
  ReachExtraction, ExtractionDraft, BlockerType
} from '../../services/reachService';

type SelectedMap = Record<string, boolean>;

function initSelected(items: any[]): SelectedMap {
  return Object.fromEntries(items.map((_, i) => [String(i), true]));
}

export const ExtractionReviewPage: React.FC = () => {
  const { projectId, sessionId } = useParams<{ projectId: string; sessionId: string }>();
  const navigate = useNavigate();
  const [extraction, setExtraction] = useState<ReachExtraction | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStakeholders, setSelectedStakeholders] = useState<SelectedMap>({});
  const [selectedBlockers, setSelectedBlockers] = useState<SelectedMap>({});
  const [selectedChampions, setSelectedChampions] = useState<SelectedMap>({});
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [importCounts, setImportCounts] = useState<{ stakeholders: number; blockers: number } | null>(null);

  useEffect(() => {
    if (!projectId || !sessionId) return;
    reachExtractionsAPI.get(projectId, sessionId)
      .then(r => {
        setExtraction(r.data.extraction);
        const d = r.data.extraction.draft;
        if (d) {
          setSelectedStakeholders(initSelected(d.stakeholders));
          setSelectedBlockers(initSelected(d.blockers));
          setSelectedChampions(initSelected(d.championCandidates));
        }
      })
      .finally(() => setLoading(false));
  }, [projectId, sessionId]);

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<SelectedMap>>,
    key: string
  ) => setter(prev => ({ ...prev, [key]: !prev[key] }));

  const confirm = async () => {
    if (!projectId || !sessionId || !extraction?.draft) return;
    setConfirming(true);
    try {
      const d: ExtractionDraft = extraction.draft;

      const stakeholderPromises = d.stakeholders
        .filter((_, i) => selectedStakeholders[String(i)])
        .map(s => reachStakeholdersAPI.create(projectId, {
          name: s.name,
          role: s.role,
          department: s.function,
          adoptionCurve: 'innovator',
          x: Math.min(0.9, Math.max(0.1, (s.influence ?? 2) / 4)),
          y: Math.min(0.9, Math.max(0.1, (s.readiness ?? 2) / 4)),
          color: '#6366f1',
        }));

      const blockerPromises = d.blockers
        .filter((_, i) => selectedBlockers[String(i)])
        .map(b => reachBlockersAPI.create(projectId, {
          type: b.type as BlockerType,
          description: b.description,
          raisedInWeek: 1,
          status: 'open',
          aiSuggestionSource: 'both',
        }));

      const [sResults, bResults] = await Promise.all([
        Promise.allSettled(stakeholderPromises),
        Promise.allSettled(blockerPromises),
      ]);

      await reachExtractionsAPI.confirm(projectId, sessionId);

      setImportCounts({
        stakeholders: sResults.filter(r => r.status === 'fulfilled').length,
        blockers: bResults.filter(r => r.status === 'fulfilled').length,
      });
      setDone(true);
    } finally {
      setConfirming(false);
    }
  };

  if (!projectId || !sessionId) return null;

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <Loader2 size={24} className="animate-spin text-slate-300" />
    </div>
  );

  if (!extraction) return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center">
        <AlertCircle size={32} className="text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 text-sm">Extraction not found.</p>
      </div>
    </div>
  );

  const d = extraction.draft;
  const isConfirmed = extraction.status === 'confirmed';

  if (done && importCounts) {
    return (
      <div className="flex-1 overflow-y-auto p-8 font-display">
        <div className="w-full max-w-[1400px] mx-auto flex flex-col items-center justify-center gap-5 py-16">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
            <CheckCircle size={28} className="text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-navy">Extraction confirmed</h2>
          <p className="text-slate-500 text-sm text-center max-w-md">
            Imported {importCounts.stakeholders} stakeholder{importCounts.stakeholders !== 1 ? 's' : ''} and {importCounts.blockers} blocker{importCounts.blockers !== 1 ? 's' : ''} into REACH.
          </p>
          <div className="flex gap-3">
            <button onClick={() => navigate(`/projects/${projectId}/reach/stakeholder-map`)}
              className="flex items-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors">
              <Users size={15} /> View Stakeholder Map
            </button>
            <button onClick={() => navigate(`/projects/${projectId}/reach/blockers`)}
              className="flex items-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
              <ShieldAlert size={15} /> View Blockers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <TrendingUp size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">REACH Extraction Review</h1>
            <p className="text-slate-500 text-sm font-medium">
              {isConfirmed ? 'Confirmed extraction' : 'Review AI-identified items. Deselect anything to exclude before importing.'}
            </p>
          </div>
          {!isConfirmed && d && !d.isEmpty && (
            <div className="ml-auto">
              <button
                onClick={confirm}
                disabled={confirming}
                className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
              >
                {confirming ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                {confirming ? 'Importing…' : 'Confirm and import'}
              </button>
            </div>
          )}
        </header>

        {(!d || d.isEmpty) && (
          <div className="text-center py-16">
            <AlertCircle size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No extractable items found in this session transcript.</p>
          </div>
        )}

        {d && !d.isEmpty && (
          <div className="flex flex-col gap-6">
            {/* Stakeholders */}
            {d.stakeholders.length > 0 && (
              <Section
                icon={Users}
                title="Stakeholders"
                count={d.stakeholders.length}
                selectedCount={Object.values(selectedStakeholders).filter(Boolean).length}
                locked={isConfirmed}
              >
                {d.stakeholders.map((s, i) => (
                  <ItemCard
                    key={s.name}
                    selected={isConfirmed ? true : !!selectedStakeholders[String(i)]}
                    onToggle={isConfirmed ? undefined : () => toggle(setSelectedStakeholders, String(i))}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-navy">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.role} · {s.function}</p>
                      {s.signals?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-1 italic">"{s.signals[0]}"</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0 text-xs text-slate-400">
                      <span>Influence {s.influence}/4</span>
                      <span>Readiness {s.readiness}/4</span>
                    </div>
                  </ItemCard>
                ))}
              </Section>
            )}

            {/* Blockers */}
            {d.blockers.length > 0 && (
              <Section
                icon={ShieldAlert}
                title="Blockers"
                count={d.blockers.length}
                selectedCount={Object.values(selectedBlockers).filter(Boolean).length}
                locked={isConfirmed}
              >
                {d.blockers.map((b, i) => (
                  <ItemCard
                    key={b.description}
                    selected={isConfirmed ? true : !!selectedBlockers[String(i)]}
                    onToggle={isConfirmed ? undefined : () => toggle(setSelectedBlockers, String(i))}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-navy">{b.description}</p>
                      <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">
                        {b.type}
                      </span>
                    </div>
                  </ItemCard>
                ))}
              </Section>
            )}

            {/* Champion candidates */}
            {d.championCandidates.length > 0 && (
              <Section
                icon={Star}
                title="Champion Candidates"
                count={d.championCandidates.length}
                selectedCount={Object.values(selectedChampions).filter(Boolean).length}
                locked={isConfirmed}
                note="For reference only — add manually as stakeholders if needed."
              >
                {d.championCandidates.map((c, i) => (
                  <ItemCard
                    key={c.name}
                    selected={isConfirmed ? true : !!selectedChampions[String(i)]}
                    onToggle={isConfirmed ? undefined : () => toggle(setSelectedChampions, String(i))}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-navy">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.role}</p>
                      {c.rationale && <p className="text-xs text-slate-400 mt-1">{c.rationale}</p>}
                    </div>
                  </ItemCard>
                ))}
              </Section>
            )}

            {/* Change impacts — display only */}
            {d.changeImpacts.length > 0 && (
              <Section
                icon={Layers}
                title="Change Impacts"
                count={d.changeImpacts.length}
                selectedCount={d.changeImpacts.length}
                locked={true}
                note="For reference only."
              >
                {d.changeImpacts.map((ci, i) => (
                  <ItemCard key={ci.function} selected={true}>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-navy">{ci.function}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{ci.currentState} → {ci.futureState}</p>
                    </div>
                    <span className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                      Impact {ci.impactRating}/4
                    </span>
                  </ItemCard>
                ))}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface SectionProps {
  icon: React.ElementType;
  title: string;
  count: number;
  selectedCount: number;
  locked: boolean;
  note?: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon: Icon, title, count, selectedCount, locked, note, children }) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary flex-shrink-0">
        <Icon size={16} />
      </div>
      <div>
        <p className="text-sm font-bold text-navy">{title}</p>
        {!locked && <p className="text-xs text-slate-400">{selectedCount} of {count} selected</p>}
        {note && <p className="text-xs text-slate-400">{note}</p>}
      </div>
    </div>
    <div className="flex flex-col gap-2">
      {children}
    </div>
  </div>
);

interface ItemCardProps {
  selected: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

const ItemCard: React.FC<ItemCardProps> = ({ selected, onToggle, children }) => (
  <div
    onClick={onToggle}
    className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
      onToggle ? 'cursor-pointer' : ''
    } ${
      selected
        ? 'border-primary/30 bg-primary/[0.03]'
        : 'border-[#e2e8f0] bg-slate-50 opacity-50'
    }`}
  >
    {onToggle && (
      <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-all ${
        selected ? 'bg-primary border-primary' : 'border-slate-300 bg-white'
      }`}>
        {selected && <CheckCircle size={10} className="text-white" />}
      </div>
    )}
    {children}
  </div>
);

export default ExtractionReviewPage;
